import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Utensils, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, RefreshCw, AlertCircle, Maximize2 } from 'lucide-react';
import { DateRange } from '../common/DateRangePicker';
import { useNutritionSummary } from '../../hooks/useNutritionSummary';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * NUTRITION TILE - Compact nutrition summary for Client Summary View
 *
 * Phase 9f: Horizontal layout with:
 * - Left: Weight change for period (placeholder for future)
 * - Center: Average calories + days logged
 * - Right: Macro donut chart with hover tooltips
 *
 * Collapsible to save space.
 */

interface NutritionTileProps {
  clientId: string;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  onExpand?: () => void;
  externalMonth?: { month: number; year: number } | null;
  onMonthChange?: (month: number, year: number) => void;
}

// Period options for dropdown
const periodOptions = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
];

// Helper to get date range for period
function getDateRangeForPeriod(days: number): DateRange {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - (days - 1));

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
    preset: days === 7 ? 'last7' : days === 14 ? 'last14' : 'last30'
  };
}

interface WeightChange {
  startWeight: number | null;
  endWeight: number | null;
  change: number | null;
}

export const NutritionTile: React.FC<NutritionTileProps> = ({ clientId, onExpand, externalMonth, onMonthChange }) => {
  // Collapse state - persist to localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('nutritionTileCollapsed');
    return saved === 'true';
  });

  // Period selection (7, 14, or 30 days) or month navigation
  const [period, setPeriod] = useState(30);
  const [monthOverride, setMonthOverride] = useState<{ year: number; month: number } | null>(null);

  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const dateRange = useMemo(() => {
    if (monthOverride) {
      const first = new Date(monthOverride.year, monthOverride.month, 1);
      const last = new Date(monthOverride.year, monthOverride.month + 1, 0);
      return { startDate: fmt(first), endDate: fmt(last), preset: 'custom' };
    }
    return getDateRangeForPeriod(period);
  }, [period, monthOverride]);

  const navigateMonth = (direction: -1 | 1) => {
    const current = monthOverride
      ? new Date(monthOverride.year, monthOverride.month + direction, 1)
      : (() => {
          const ref = new Date(dateRange.startDate + 'T00:00:00');
          return new Date(ref.getFullYear(), ref.getMonth() + direction, 1);
        })();
    setMonthOverride({ year: current.getFullYear(), month: current.getMonth() });
    onMonthChange?.(current.getMonth(), current.getFullYear());
  };

  const getMonthLabel = (): string => {
    const start = new Date(dateRange.startDate + 'T00:00:00');
    return start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const handlePeriodChange = (newPeriod: number) => {
    setPeriod(newPeriod);
    setMonthOverride(null); // Reset month override when switching to period mode
  };

  // Sync with calendar month changes from parent
  useEffect(() => {
    if (externalMonth) {
      setMonthOverride(externalMonth);
    }
  }, [externalMonth?.month, externalMonth?.year]);

  const [syncing, setSyncing] = useState(false);
  const [weeklyWeight, setWeeklyWeight] = useState<WeightChange>({ startWeight: null, endWeight: null, change: null });

  const { summary, connectionStatus, loading, error, triggerSync } = useNutritionSummary(
    clientId,
    dateRange
  );

  // Fetch weight change from weight-progress endpoint (per-client, not dashboard)
  useEffect(() => {
    const fetchWeeklyWeight = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const response = await fetch(`/api/coach/clients/${clientId}/weight-progress`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.weeklyChange !== null) {
            setWeeklyWeight({
              startWeight: data.previousWeek?.average || null,
              endWeight: data.currentWeek?.average || null,
              change: data.weeklyChange
            });
          }
        }
      } catch (err) {
        console.error('Error fetching weekly weight:', err);
      }
    };

    if (clientId) {
      fetchWeeklyWeight();
    }
  }, [clientId]);

  // Persist collapse state
  useEffect(() => {
    localStorage.setItem('nutritionTileCollapsed', String(isCollapsed));
  }, [isCollapsed]);

  const handleSync = async () => {
    setSyncing(true);
    await toast.promise(triggerSync(), {
      loading: 'Syncing from FatSecret...',
      success: (result: any) => {
        const daysChecked = (result.syncedDays || 0) + (result.skippedDays || 0);
        const daysChanged = (result.syncedDays || 0) - (result.unchangedDays || 0);
        const detail = daysChanged > 0
          ? `${daysChanged} days updated`
          : 'Everything up to date';
        return `Nutrition Data Updated\nChecked ${daysChecked} days · ${detail}`;
      },
      error: 'Sync failed'
    });
    setSyncing(false);
  };

  // Macro donut chart data
  const donutData = useMemo(() => {
    if (!summary) return null;

    const protein = summary.averages.protein_g || 0;
    const carbs = summary.averages.carbs_g || 0;
    const fat = summary.averages.fats_g || 0;

    return {
      labels: ['Protein', 'Carbs', 'Fat'],
      datasets: [{
        data: [protein, carbs, fat],
        backgroundColor: ['#ef4444', '#f59e0b', '#3b82f6'],
        borderWidth: 0,
        hoverOffset: 4,
      }]
    };
  }, [summary]);

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      }
    }
  };

  // Not connected state
  if (connectionStatus && !connectionStatus.connected) {
    return (
      <div className="nutrition-tile">
        <div className="nutrition-tile-header" onClick={() => setIsCollapsed(!isCollapsed)}>
          <div className="header-left">
            <Utensils size={14} />
            <h4>Nutrition</h4>
            <span className="status-badge disconnected">Not Connected</span>
          </div>
          <div className="header-buttons">
            {onExpand && (
              <button
                className="maximize-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onExpand();
                }}
                title="Expand to full view"
              >
                <Maximize2 size={16} />
              </button>
            )}
            <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
              {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </div>
        </div>

        {!isCollapsed && (
          <div className="nutrition-tile-body">
            <div className="not-connected-state">
              <AlertCircle size={24} />
              <p>FatSecret not connected</p>
              <span className="hint">Client needs to connect their FatSecret account to view nutrition data.</span>
            </div>
          </div>
        )}

        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="nutrition-tile">
      {/* Header - Always visible */}
      <div className="nutrition-tile-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="header-left">
          <Utensils size={14} />
          <h4>Nutrition</h4>
          {connectionStatus?.connected && (
            <span className="status-badge connected">Connected</span>
          )}
        </div>
        <div className="header-controls">
          <select
            className="period-dropdown"
            value={monthOverride ? '' : period}
            onChange={(e) => handlePeriodChange(Number(e.target.value))}
            onClick={(e) => e.stopPropagation()}
          >
            {monthOverride && <option value="">Month</option>}
            {periodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <div className="month-nav-tile" onClick={(e) => e.stopPropagation()}>
            <button className="month-nav-btn-tile" onClick={() => navigateMonth(-1)} title="Previous month">
              <ChevronLeft size={14} />
            </button>
            <button className="month-nav-label-tile" onClick={() => {
              const now = new Date();
              setMonthOverride({ year: now.getFullYear(), month: now.getMonth() });
              onMonthChange?.(now.getMonth(), now.getFullYear());
            }} title="Go to current month">{getMonthLabel()}</button>
            <button className="month-nav-btn-tile" onClick={() => navigateMonth(1)} title="Next month">
              <ChevronRight size={14} />
            </button>
          </div>
          <button
            className="sync-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleSync();
            }}
            disabled={syncing}
            title="Sync from FatSecret"
          >
            <RefreshCw size={14} className={syncing ? 'spinning' : ''} />
          </button>
          {onExpand && (
            <button
              className="maximize-btn"
              onClick={(e) => {
                e.stopPropagation();
                onExpand();
              }}
              title="Expand to full view"
            >
              <Maximize2 size={16} />
            </button>
          )}
          <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className="nutrition-tile-body">
          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner-small" />
              Loading...
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="error-state">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Horizontal Stats Layout */}
          {!loading && !error && summary && summary.totals.days_with_data > 0 && (
            <div className="horizontal-stats">
              {/* Left: Average calories */}
              <div className="stat-section calories-section-left">
                <span className="stat-big calories">{summary.averages.calories.toLocaleString()}</span>
                <span className="stat-sub">cal/day avg</span>
              </div>

              {/* Center: Weekly weight change */}
              <div className="stat-section weight-section-center">
                {weeklyWeight.change !== null ? (
                  <>
                    <span className={`stat-big weight ${weeklyWeight.change < 0 ? 'loss' : weeklyWeight.change > 0 ? 'gain' : ''}`}>
                      {weeklyWeight.change > 0 ? '+' : ''}{weeklyWeight.change} lbs
                    </span>
                    <span className="stat-sub">this week</span>
                  </>
                ) : (
                  <>
                    <span className="stat-big muted">--</span>
                    <span className="stat-sub">no weight data</span>
                  </>
                )}
              </div>

              {/* Right: Macro donut */}
              <div className="stat-section donut-section">
                {donutData && (
                  <div className="donut-container">
                    <Doughnut data={donutData} options={donutOptions} />
                  </div>
                )}
                <div className="macro-legend">
                  <div className="legend-row"><span className="legend-box protein">P</span><span className="legend-value">{summary.averages.protein_g}g</span></div>
                  <div className="legend-row"><span className="legend-box carbs">C</span><span className="legend-value">{summary.averages.carbs_g}g</span></div>
                  <div className="legend-row"><span className="legend-box fat">F</span><span className="legend-value">{summary.averages.fats_g}g</span></div>
                </div>
              </div>
            </div>
          )}

          {/* No data state */}
          {!loading && !error && summary && summary.totals.days_with_data === 0 && (
            <div className="no-data-state">
              <p>No nutrition data for this period</p>
              <span className="hint">Try syncing or selecting a different date range.</span>
            </div>
          )}
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .nutrition-tile {
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-large);
    overflow: hidden;
    height: fit-content;
    flex-shrink: 0;
  }

  .nutrition-tile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .nutrition-tile-header:hover {
    background: var(--bg-tertiary);
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .header-left svg {
    color: var(--text-secondary);
  }

  .nutrition-tile-header h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .status-badge {
    font-size: 10px;
    font-weight: 500;
    padding: 2px 6px;
    border-radius: 8px;
  }

  .status-badge.connected {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  }

  .status-badge.disconnected {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
  }

  .header-controls {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .period-dropdown {
    padding: 6px 10px;
    font-size: 12px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-small);
    color: var(--text-primary);
    cursor: pointer;
  }

  .period-dropdown:focus {
    outline: none;
    border-color: var(--accent-green);
  }

  .month-nav-tile {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .month-nav-btn-tile {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: transparent;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-small, 4px);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s;
    padding: 0;
  }

  .month-nav-btn-tile:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    border-color: var(--accent-green);
  }

  .month-nav-label-tile {
    font-size: 11px;
    font-weight: 500;
    color: var(--text-primary);
    width: 70px;
    text-align: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 0;
    transition: color 0.15s;
  }

  .month-nav-label-tile:hover {
    color: var(--accent-green);
  }

  .sync-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: none;
    border: none;
    border-radius: 4px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s;
  }

  .sync-btn:hover:not(:disabled) {
    background: var(--bg-tertiary);
    color: var(--accent-green);
  }

  .sync-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .sync-btn .spinning {
    animation: spin 1s linear infinite;
  }

  .maximize-btn, .collapse-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s;
  }

  .maximize-btn:hover {
    background: var(--accent-green-bg);
    color: var(--accent-green);
  }

  .collapse-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  .nutrition-tile-body {
    padding: 0 16px 12px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 20px;
    color: var(--text-secondary);
    font-size: 12px;
  }

  .loading-spinner-small {
    width: 14px;
    height: 14px;
    border: 2px solid var(--border-secondary);
    border-top-color: var(--accent-green);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .error-state {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    background: rgba(239, 68, 68, 0.1);
    border-radius: var(--radius-medium);
    color: #ef4444;
    font-size: 11px;
  }

  .not-connected-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 12px;
    text-align: center;
    color: var(--text-secondary);
  }

  .not-connected-state svg {
    color: var(--text-tertiary);
    margin-bottom: 8px;
  }

  .not-connected-state p {
    margin: 0 0 4px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .not-connected-state .hint {
    font-size: 11px;
    color: var(--text-tertiary);
  }

  /* Horizontal Stats Layout */
  .horizontal-stats {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
  }

  .stat-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .stat-big {
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.2;
  }

  .stat-big.calories {
    font-size: 42px;
    color: var(--accent-green);
  }

  .stat-sub {
    font-size: 11px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .calories-section-left {
    min-width: 100px;
  }

  .weight-section-center {
    flex: 1;
    border-left: 1px solid var(--border-secondary);
    border-right: 1px solid var(--border-secondary);
    padding: 0 16px;
    align-items: flex-start;
  }

  .stat-big.weight {
    font-size: 40px;
  }

  .stat-big.weight.loss {
    color: #10b981;
  }

  .stat-big.weight.gain {
    color: #ef4444;
  }

  .stat-big.muted {
    color: var(--text-tertiary);
  }

  .donut-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 110px;
  }

  .donut-container {
    width: 72px;
    height: 72px;
    position: relative;
    z-index: 10;
  }

  .donut-container canvas {
    position: relative;
    z-index: 10;
  }

  .macro-legend {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 52px;
  }

  .legend-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  .legend-box {
    width: 18px;
    height: 18px;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
  }

  .legend-box.protein {
    background: #ef4444;
  }

  .legend-box.carbs {
    background: #f59e0b;
  }

  .legend-box.fat {
    background: #3b82f6;
  }

  .legend-value {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    text-align: right;
    min-width: 28px;
  }

  .no-data-state {
    text-align: center;
    padding: 16px;
    color: var(--text-secondary);
  }

  .no-data-state p {
    margin: 0 0 4px;
    font-size: 12px;
  }

  .no-data-state .hint {
    font-size: 10px;
    color: var(--text-tertiary);
  }
`;

export default NutritionTile;
